import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Card, Statistic, List, Avatar, Spin, Button, Tooltip, message } from 'antd';
import {
    TeamOutlined,
    PhoneOutlined,
    MessageOutlined,
    CheckCircleOutlined,
    UserOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { pbxService } from '../services/api';
import { useSoftphone } from '../context/SoftphoneContext';

const { Title, Text } = Typography;

const Dashboard = () => {
    const { user } = useAuth();
    const { makeCall, status, inCall, isCalling } = useSoftphone();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const fetchStats = async () => {
            try {
                const response = await pbxService.getDashboardStats();
                if (isMounted && response.status === 'success') {
                    setStats(response.data);
                }
            } catch (error) {
                console.error("Error fetching dashboard stats", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchStats(); // Initial fetch

        const intervalId = setInterval(fetchStats, 2000); // Poll every 2 seconds

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, []);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: 100 }}>
                <Spin size="large" tip="Cargando estadísticas..." />
            </div>
        );
    }

    const metrics = stats || {
        calls_made: 0,
        calls_received: 0,
        contacts_count: 0,
        effectiveness: 0,
        last_interactions: []
    };

    return (
        <div>
            <Title level={2}>Dashboard de Agente</Title>
            <Text type="secondary">Bienvenido al sistema, {user?.name || 'Agente'}. Aquí tienes un resumen de tu actividad hoy.</Text>

            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} hoverable>
                        <Statistic
                            title="Llamadas Realizadas"
                            value={metrics.calls_made}
                            prefix={<PhoneOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} hoverable>
                        <Statistic
                            title="Llamadas Recibidas"
                            value={metrics.calls_received}
                            prefix={<PhoneOutlined rotate={180} />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} hoverable>
                        <Statistic
                            title="Participantes Contactados"
                            value={metrics.contacts_count}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} hoverable>
                        <Statistic
                            title="Efectividad"
                            value={metrics.effectiveness}
                            suffix="%"
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col span={24}>
                    <Card title="Últimas Interacciones" bordered={false}>
                        <List
                            itemLayout="horizontal"
                            locale={{ emptyText: 'No hay interacciones recientes hoy' }}
                            dataSource={metrics.last_interactions}
                            renderItem={(item) => (
                                <List.Item
                                    actions={[
                                        <Tooltip title="Volver a llamar">
                                            <Button
                                                type="primary"
                                                shape="circle"
                                                icon={<PhoneOutlined />}
                                                size="small"
                                                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                disabled={status !== 'registered' || inCall || isCalling}
                                                onClick={() => {
                                                    if (item.phone) {
                                                        message.info(`Llamando a ${item.phone}...`);
                                                        makeCall(item.phone);
                                                    } else {
                                                        message.warning('No se encontró un número válido para llamar.');
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={<Avatar icon={<UserOutlined />} />}
                                        title={<Text strong>Llamada {item.direction === 'outbound' ? 'a' : 'de'} {item.participant_name || 'Desconocido'}</Text>}
                                        description={
                                            <div>
                                                <Text type="secondary" style={{ marginRight: 8 }}><PhoneOutlined /> {item.phone}</Text>
                                                <br />
                                                <Text type="secondary">
                                                    {item.direction === 'outbound' ? 'Saliente' : 'Entrante'} - {item.campaign_name || 'Sin campaña'} - {item.status_es}
                                                </Text>
                                            </div>
                                        }
                                    />
                                    <div style={{ textAlign: 'right', minWidth: 80 }}>
                                        <Text strong display="block">{item.time}</Text>
                                        <br />
                                        <Text type="secondary">{item.duration}s</Text>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Dashboard;
